import { apiFetch } from '@Shared/Api/apiClient';
import React from 'react';
import { func, string, bool } from 'prop-types';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { GLOBAL } from '@Config/global';
import { DEFAULT_HEADER } from '@Config/userToken';
import { debounce } from '@Shared/Utils/helpers';
import './styles.scss';

const PER_PAGE = 50;

class Typeahead extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      options: [],
      query: ''
    };
    this._cache = {};
    this._abortCtrl = null;
    this._handleSearch = debounce(this._handleSearch.bind(this), 400);
  }

  componentWillUnmount() {
    this._handleSearch.cancel();
    this._abortCtrl?.abort();
  }

  async makeAndHandleRequest(query, page = 1) {
    this._abortCtrl?.abort();
    this._abortCtrl = new AbortController();

    const url = `${this.props.SEARCH_URI}&q=${encodeURIComponent(
      query.toUpperCase()
    )}&page=${page}&per_page=${PER_PAGE}`;

    const resp = await apiFetch(url, {
      method: 'GET',
      headers: DEFAULT_HEADER.get(),
      signal: this._abortCtrl.signal
    });

    const { items, total_count } = await resp.json();
    const options = (items || []).map(i => ({
      id: i.id,
      value: i.value,
      message: i.message
    }));
    return { options, total_count };
  }

  render() {
    const { COMMON_SEARCH_FORM } = GLOBAL;

    return (
      <div className="input">
        <label>{this.props.label}</label>
        <AsyncTypeahead
          isLoading={this.state.isLoading}
          options={this.state.options}
          id={this.props.id}
          className={`input-group input-group-${this.props.borderColor} rounded`}
          labelKey={option => `${option[this.props.labelKey]}`}
          maxResults={PER_PAGE - 1}
          minLength={2}
          onInputChange={this._handleInputChange}
          onPaginate={this._handlePagination}
          onSearch={this._handleSearch}
          onChange={this._handleChange}
          paginate
          searchText={COMMON_SEARCH_FORM.SEARCHING_TEXT}
          emptyLabel={COMMON_SEARCH_FORM.EMPTY_LABEL_TEXT}
          promptText={COMMON_SEARCH_FORM.PROMPT_TEXT}
          placeholder={this.props.placeholder}
          disabled={this.props.disabled}
          selected={this.props.selected} 
          renderMenuItemChildren={option => (
            <div key={option.id}>
              <span style={{ fontSize: '14px' }}>
                {option[this.props.labelKey]}
              </span>
            </div>
          )}
          useCache={false}
        />
        {this.props.errorMsg && this.props.errorMsg.length ? (
          <span className={`input-span-${this.props.borderColor}`}>
            {this.props.errorMsg}
          </span>
        ) : null}
      </div>
    );
  }

  _handleInputChange = query => {
    // Cancelar búsqueda anterior si el usuario sigue escribiendo
    this._abortCtrl?.abort();
    
    // Limpiar selección anterior
    this.props.onClean();
    
    // Actualizar estado del query
    this.setState({ query });
    
    // Si el query es muy corto, limpiar opciones y loading
    if (!query || query.trim().length < 2) {
      this.setState({ 
        isLoading: false, 
        options: [] 
      });
    }
  };

  async _handlePagination(e, shownResults) {
    const { query } = this.state;
    const cached = this._cache[query];

    if (!cached || !query) return;

    // Evitar paginación si ya tenemos todos los resultados
    if (cached.options.length > shownResults || cached.options.length === cached.total_count) {
      return;
    }

    // Evitar múltiples paginaciones simultáneas
    if (this.state.isLoading) return;

    this.setState({ isLoading: true });
    const page = cached.page + 1;

    try {
      const resp = await this.makeAndHandleRequest(query, page);
      const options = cached.options.concat(resp.options);
      this._cache[query] = { ...cached, options, page };
      this.setState({ isLoading: false, options });
    } catch (error) {
      // No mostrar error si la búsqueda fue cancelada
      if (error.name !== 'AbortError') {
        this.setState({ isLoading: false });
      }
    }
  }

  async _handleSearch(query) {
    // Cancelar búsqueda anterior si existe
    this._abortCtrl?.abort();
    
    // Limpiar estado si la consulta está vacía
    if (!query || query.trim().length < 2) {
      this.setState({ 
        isLoading: false, 
        options: [],
        query: ''
      });
      return;
    }

    // Verificar cache primero
    if (this._cache[query]) {
      this.setState({ 
        options: this._cache[query].options,
        isLoading: false,
        query
      });
      return;
    }

    // Iniciar búsqueda
    this.setState({ 
      isLoading: true,
      query,
      options: [] // Limpiar opciones anteriores durante la búsqueda
    });

    try {
      const resp = await this.makeAndHandleRequest(query, 1);
      this._cache[query] = { ...resp, page: 1 };
      this.setState({ 
        isLoading: false, 
        options: resp.options 
      });
    } catch (error) {
      // No mostrar error si la búsqueda fue cancelada
      if (error.name !== 'AbortError') {
        this.setState({ 
          isLoading: false, 
          options: [] 
        });
      }
    }
  }

  _handleChange = selected => {
    // Cancelar cualquier búsqueda en curso cuando el usuario selecciona
    this._abortCtrl?.abort();
    
    // Limpiar estado de loading
    this.setState({ isLoading: false });
    
    if (selected.length) {
      this.props.onSelected(selected[0]);
    } else {
      // Si se limpia la selección, llamar a onClean
      this.props.onClean();
    }
  };
}

Typeahead.propTypes = {
  id: string.isRequired,
  label: string.isRequired,
  placeholder: string.isRequired,
  labelKey: string.isRequired,
  borderColor: string.isRequired,
  errorMsg: string.isRequired,
  onSelected: func,
  onClean: func,
  disabled: bool,
  SEARCH_URI: string.isRequired
};

Typeahead.defaultProps = {
  label: '',
  placeholder: '',
  labelKey: 'value',
  borderColor: '',
  errorMsg: '',
  disabled: false,
  onSelected: () => {},
  onClean: () => {}
};

export default Typeahead;
